import BarChartIcon from '@mui/icons-material/BarChart';
import PrintIcon from '@mui/icons-material/Print';
import {
	Box,
	Button,
	Card,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	Stack
} from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { setCyclesStatisticsCharts } from '../actions';
import ActionIconButton from '../components/common/ActionIconButton';
import ChartComponent from '../components/common/ChartComponent';
import LoadingDialog from '../components/common/LoadingDialog';
import consts from '../consts';
import { useDeepMemo, useSetState, useUniqueEffect } from '../hooks';
import connectionService from '../services/Connection';
import util from '../util';
import { usePrerequisites } from '../contexts/Prerequisites';
import CycleListGraphForm from './forms/CycleListGraphForm';


const Graph = () => {
	const [{ strings }] = usePrerequisites();
	const dispatch = useDispatch();
	const { statisticsCharts } = useSelector((dataStore) => dataStore.cycles);

	const defaultChartConfigsRef = useRef(
		[
			{
				chartIndex: 1,
				selectedChartType: 'Stacked',
				selectedChartValue: 'RecordCount',
				showOnEquipmentScreen: true,
				selectedChartValueField: strings.CycleStatus,
				stackedChartGroup1: 'CycleStatus',
				stackedChartGroup2: 'OperatorName',
				chartTitle: 'CyclGrpOpr'
			},
			{
				chartIndex: 2,
				selectedChartType: 'Column',
				selectedChartValue: 'RecordCount',
				showOnEquipmentScreen: true,
				selectedChartValueField: strings.Operator,
				selectedChartGroup: 'OperatorName',
				chartTitle: 'CyclGrpOprator'
			},
			{
				chartIndex: 3,
				selectedChartType: 'Pie',
				selectedChartValue: 'RecordCount',
				showOnEquipmentScreen: true,
				selectedChartValueField: strings.CycleType,
				selectedChartGroup: 'CycleName',
				chartTitle: 'CyclGrpCyTyp'
			},
			{
				chartIndex: 4,
				selectedChartType: 'Stacked',
				selectedChartValue: 'RecordCount',
				showOnEquipmentScreen: false,
				selectedChartValueField: strings.EquipmentList,
				stackedChartGroup1: 'EquipmentName',
				stackedChartGroup2: 'CycleName',
				chartTitle: 'CyclGrpEqThCyTy'
			},
			{
				chartIndex: 5,
				selectedChartType: 'Pie',
				selectedChartValue: 'RecordCount',
				showOnEquipmentScreen: false,
				selectedChartValueField: strings.EquipmentType,
				selectedChartGroup: 'EquipmentTypeName',
				chartTitle: 'CyclGrpEqTyp'
			},
			{
				chartIndex: 6,
				selectedChartType: 'Pie',
				selectedChartValue: 'RecordCount',
				showOnEquipmentScreen: true,
				selectedChartValueField: strings.ReviewStatus,
				selectedChartGroup: 'CycleActionStatus',
				chartTitle:'CyclReview'
			},
			{
				chartIndex: 7,
				selectedChartType: 'Stacked',
				selectedChartValue: 'RecordCount',
				showOnEquipmentScreen: true,
				selectedChartValueField: strings.Day,
				stackedChartGroup1: 'Day',
				stackedChartGroup2: 'CycleStatus',
				chartTitle: 'CyclDay'
			}
		
	]);

	const [state, setState] = useSetState({
		loadingList: [],
		loading: false,
		advanceSearch: '',
		preferences: {},
		configLoads: [],
		preferencesLoaded: false,
		activeRecord: null
	});

	const fetchConfigData = useCallback((config, index) => {

		if (config.selectedChartType === 'Stacked') {
			if (!config.stackedChartGroup1) {
				// Swal.fire({ titleText: strings.Alert, text: strings.GroupByValue, icon: 'error' });
				throw new Error(strings.GroupByValue);
			}
			if (!config.stackedChartGroup2) {
				// Swal.fire({ titleText: strings.Alert, text: strings.ThenSecondLevel, icon: 'error' });
				throw new Error(strings.ThenSecondLevel);
			}
		}
		setState((prevState) => {
			return {
				loadingList: { ...prevState.loadingList, [index]: true }
			};
		});
		let selectedValue = config.selectedChartGroup;
		if (config.selectedChartType === 'Stacked') {
			selectedValue = `${config.stackedChartGroup1},${config.stackedChartGroup2}`;
		}
		let formData = new FormData();
		const chartType = config.selectedChartType ? config.selectedChartType : 'pie';
		formData.append('chartType', chartType);
		formData.append('action', 'otherAction');
		formData.append('otherAction', 'LoadChart');
		formData.append('equipmentId', state.equipmentId || 0);
		formData.append('groupBy', selectedValue);

		if (state.advanceSearch) {
			formData.append('AdvanceSearch', state.advanceSearch);
		}
		let tableFilters = state.tableFilters
		let dateTimeFormat = util.getDateTimeFormat();
		const isForDate = config.stackedChartGroup1 === 'Day' || config.stackedChartGroup2 === 'Day';
		const filters = util.getStartTimeAndFilter(state.tableFilters, isForDate);
		tableFilters = tableFilters && !tableFilters.cancelable ? tableFilters : config.tableFilters;
		formData.append('selectedCycleDate', dayjs(filters.filterStartDate).format(dateTimeFormat.date));
		if (filters.filters) {
			formData.append('filter', filters.filters);
		}

		fetch(connectionService.Statistics, {
			body: formData,
			method: 'POST',
			credentials: 'include'
		})
			.then((res) => res.json())
			.then(({ data }) => {
				const total =
					['Pie', 'PieChart'].includes(config?.selectedChartType) && Array.isArray(data)
						? data.reduce((acc, entry) => acc + (entry.value || 0), 0)
						: 0;
				const configLoad = { data: data || null, label: `${total} ${strings.CycleList}` }
				setState((prevState) => ({
					configLoads: { ...prevState.configLoads, [index]: configLoad }
				}))
				return configLoad;
			})
			.finally(() => setState(prevState => ({ loadingList: { ...prevState.loadingList, [index]: false } })));
	},
		[state.advanceSearch, statisticsCharts]
	);


	const isNew = useDeepMemo(
		() =>
			Array.isArray(statisticsCharts) &&
			!statisticsCharts?.some((item) => item.chartIndex === state.activeRecord?.record.chartIndex),
		[statisticsCharts, state.activeRecord]
	);

	const updateChartConfigPreferences = useCallback(
		async (newChartConfigs) => {
			const formData = new FormData();
			const newPreferences = { ...state.preferences, defaultChartConfig: newChartConfigs };
			formData.append('id', 'Cycleen');
			formData.append('prefId', 0);
			formData.append('action', 'save');
			formData.append('prefName', 'StatisticsGraph');
			formData.append('isDefault', false);
			formData.append('prefValue', JSON.stringify(newPreferences));
			const res = await fetch(connectionService.GridPreferenceManager, {
				body: formData,
				method: 'POST',
				credentials: 'include'
			});
			setState({
				preferences: newPreferences
			});
			return res;
		},
		[state.preferences]
	);

	const handleOnChartConfigSubmit = useCallback(
		(values, { resetForm }) => {
			if (values.selectedChartType === 'Stacked') {
				if (!values.stackedChartGroup1) {
					Swal.fire({ titleText: strings.Alert, text: strings.GroupByValue, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false });
					return;
				}
				if (!values.stackedChartGroup2) {
					Swal.fire({ titleText: strings.Alert, text: strings.ThenSecondLevel, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false });
					return;
				}
			} else if (!values.selectedChartGroup) {
				Swal.fire({ titleText: strings.Alert, text: strings.ValidGrouping, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false });
				return;
			}
			const newChartConfigs = [...statisticsCharts];
			const chartIndex = newChartConfigs.findIndex((item) => item.chartIndex === values?.chartIndex);
			if (chartIndex !== -1) {
				newChartConfigs[chartIndex] = values;
			} else {
				newChartConfigs.push(values);
			}
			

			setState({
				loading: true
			});
			updateChartConfigPreferences(newChartConfigs)
				.then((response) => {
					if (response.status === 200) {
						dispatch(setCyclesStatisticsCharts(newChartConfigs));
						Swal.fire({ titleText: strings.Success, text: strings.ChartAdded, icon: 'success', timeout: 3000, allowOutsideClick: false });
					}
				})
				.catch(() => {
					Swal.fire({ titleText: strings.ErrorTitle, text: strings.Status_503, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false });
				})
				.finally(() => {
					resetForm();
					setState({
						loading: false,
						activeRecord: null
					});
				});
		},
		[statisticsCharts, state.activeRecord]
	);

	const handleOnCloseModal = useCallback((...args) => {
		setState({
			activeRecord: null
		});
	}, []);

	const printPage = () => {
			const css = `
				@media print { 
					.chart-container {
						-webkit-flex-basis: 100%;
						-ms-flex-preferred-size: 100%;
						flex-basis: 100%;
						-webkit-box-flex: 1;
						-webkit-flex-grow: 1;
						-ms-flex-positive: 1;
						flex-grow: 1;
						max-width: 100%;
						height: 100%;
						page-break-after: always;
					}

					.chart-container:last-child {
						page-break-after: avoid;
					}
				}`;
			const head = document.head || document.getElementsByTagName('head')[0];
			const style = document.createElement('style');

			head.appendChild(style);
			style.type = 'text/css';
			if (style.styleSheet) {
				// This is required for IE8 and below.
				style.styleSheet.cssText = css;
			} else {
				style.appendChild(document.createTextNode(css));
			}
				window.onafterprint = () => {
					try {
						head.removeChild(style);
					} catch (error) {
						console.error('print remove child error', error);
					}
				};
				window.print();
				return true
			
		
	};

	const printChart = () => {
		printPage();
	};

	const resetCharts = useCallback(() => {
		const newChartConfigs = defaultChartConfigsRef.current;
		setState({
			loading: true
		});
		updateChartConfigPreferences(newChartConfigs)
			.then((response) => {
				if (response.status === 200) {
					dispatch(setCyclesStatisticsCharts(newChartConfigs));
					Swal.fire({ titleText: strings.Success, text: strings.ResetChart, icon: 'success', confirmButtonText: strings.Ok, allowOutsideClick: false });
				}
			})
			.catch(() => {
				Swal.fire({ titleText: strings.ErrorTitle, text: strings.Status_503, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false });
			})
			.finally(() => {
				setState({
					loading: false,
					activeRecord: null
				});
			});
	}, []);
	const handleOnRemoveChart = useCallback(
		(config) => {
			setState({
				loading: true,
				deletionChartIndex: config.chartIndex
			});
			const newChartConfigs = statisticsCharts.filter((chartConfig) => chartConfig.chartIndex !== config?.chartIndex);
			updateChartConfigPreferences(newChartConfigs)
				.then((response) => {
					if (response.status === 200) {
						dispatch(setCyclesStatisticsCharts(newChartConfigs));
						
						Swal.fire({ titleText: strings.Success, text: strings.DeleteSuccessText, icon: 'success', confirmButtonText: strings.Ok, allowOutsideClick: false });
					}
				})
				.catch(() => {
					Swal.fire({ titleText: strings.ErrorTitle, text: strings.Status_503, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false });
				})
				.finally(() => {
					setState({
						loading: false,
						activeRecord: null,
						deletionChartIndex: -1
					});
				});
		},
		[statisticsCharts]
	);
	const handleOnShowChartSettings = useCallback(
		(config) => {
			
			setState({
				activeRecord: {
					record: config,
					id: config?.chartIndex,
					chartIndex: config?.chartIndex
				}
			});
		},
		[]
	);
	const handleOnAddChart = useCallback(() => {
		const chartIndex = statisticsCharts.length + 1;
		const newConfig = {
			chartIndex,
			selectedChartValue: 'RecordCount',
			showOnEquipmentScreen: true,
			chartTitle: 'New Chart'
		};
		setState({
			activeRecord: {
				record: newConfig,
				id: chartIndex
			}
		});
	}, [statisticsCharts.length]);

	
	useUniqueEffect(() => {
		Promise.allSettled(statisticsCharts.map(fetchConfigData));
	}, []);
	return (
		<Box>
			<Dialog open={!!state.activeRecord?.record} onClose={handleOnCloseModal}>
				<DialogTitle>{strings.Settings}</DialogTitle>
				<DialogContent>
					<CycleListGraphForm
						activeRecord={{
							record: state.activeRecord?.record,
							id: state.activeRecord?.record?.chartIndex,
							combos: {
								ChartType: consts.ChartTypes,
								ChartGroup: consts.ChartGrouplookupType,
								ChartValue: consts.ChartValueType
							}
						}}
						onSubmit={handleOnChartConfigSubmit}
						containerProps={{
							elevation: 0,
							sx: {
								'& .FormTools': {
									display: 'none'
								}
							}
						}}
						loading={state.loading}
						actionsContainerProps={{
							sx: {
								padding: (theme) => theme.spacing(1, 0),
								marginTop: 2
							}
						}}
						hideCancel
						saveBtnText={isNew ? strings.AddChart : strings.Ok}
						formActions={[
							{
								type: 'button',
								text: strings.Cancel,
								variant: 'contained',
								disableelevation: true,
								sx: { backgroundColor: 'error.light', '&:hover': { backgroundColor: 'error.main' } },
								onClick: handleOnCloseModal
							}
						]}
					/>
				</DialogContent>
			</Dialog>
			<Card>
				<Grid container>
					<Grid size={12} className="print-hide" data-html2canvas-ignore="true" item>
						<LoadingDialog open={state.printing} />
						<Stack
							direction={{ xs: 'column', md: 'row' }}
							justifyContent="flex-end"
							alignItems="center"
							spacing={2}
							sx={{ margin: '1em' }}
							className="print-hide"
						>
							<Button
								variant="contained"
								sx={{
									backgroundColor: 'success.light',

									'&:hover': {
										backgroundColor: 'success.main'
									}
								}}
								size="small"
								className="no-print"
								onClick={handleOnAddChart}
								title={strings.AddChart}
								disableelevation
							>
								{strings.AddChart}
							</Button>
							<Button
								variant="contained"
								className="no-print"
								sx={{
									backgroundColor: 'success.light',
									'&:hover': {
										backgroundColor: 'success.main'
									}
								}}
								size="small"
								onClick={resetCharts}
								title={strings.ResetChart}
								disableelevation
							>
								{strings.ResetChart}
							</Button>
							<ActionIconButton
								variant="contained"
								className="no-print"
								size="small"
								color="primary"
								onClick={printChart}
								title={strings.Print}
								disableelevation
							>
								<PrintIcon fontSize="inherit" />
							</ActionIconButton>
						</Stack>
					</Grid>

					<Grid size={12} item>
						<Grid
							sx={{
								marginBottom: 1,
								marginTop: 0,
								marginLeft: 0,
								width: '100%',
								minHeight: '70vh',
								paddingRight: 1
							}}
							spacing={1}
							container
						>
							{statisticsCharts.length > 0 &&
								statisticsCharts.map((chartConfig, i) => (
									<Grid
										size={6}
										sx={{
											display: 'flex',
											...(state.deletionChartIndex > -1 &&
											chartConfig.chartIndex === state.deletionChartIndex
												? { opacity: 0.3 }
												: {})
										}}
										className="avoid-print-break chart-container"
										item
										key={`chartConfig-${i}`}
									>
										<ChartComponent
											loading={Boolean(state.loadingList[i])}
											fetchConfigData={fetchConfigData}
											advanceSearch={state.advanceSearch}
											onShowSettings={handleOnShowChartSettings}
											onRemoveChart={handleOnRemoveChart}
											config={chartConfig}
											{...state.configLoads[i]}
											index={i}
										/>
									</Grid>
								))}
							{statisticsCharts?.length === 0 && (
								<Box
									display={'flex'}
									width={'100%'}
									alignItems={'center'}
									justifyContent={'center'}
									padding={2}
								>
									<BarChartIcon fontSize={'large'} />
								</Box>
							)}
							{state.loading && (
								<Grid
									data-html2canvas-ignore="true"
									size={12}
									sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
									item
								>
									<CircularProgress size={24} />
								</Grid>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Card>
		</Box>
	);
};

export default Graph;
